import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { CameraDevice, Html5Qrcode } from 'html5-qrcode'
import { Html5QrcodeResult } from 'html5-qrcode/src/core'
import { When } from 'react-if'
import wait from 'waait'
import { Button } from '@/atoms/Button'
import { FlipIcon } from '@/atoms/Icons/FlipIcon'
import { TitleMD } from '@/atoms/Text'
import { logger } from '@/utils/logging'

export interface ScannerComponentProps {
  onSuccess: (decodedText: string, result?: Html5QrcodeResult) => void
  onError: () => void
  verbose?: boolean
}
export const ScannerComponent = ({ onSuccess, onError }: ScannerComponentProps) => {
  const html5QrCode = useRef<Html5Qrcode | null>(null)
  const [selectedCamera, setSelectedCamera] = useState<CameraDevice | null>(null)
  const [devicesList, setDevicesList] = useState<CameraDevice[]>([])
  const [shouldShowScanner, setShouldShowScanner] = useState<boolean>(true)
  const [inputValue, setInputValue] = useState<string>('')
  const [shouldShowSuccess, setShouldShowSuccess] = useState<boolean>(false)

  const handleSuccess = useCallback(
    async (e: string, r: Html5QrcodeResult) => {
      setShouldShowSuccess(true)
      html5QrCode.current?.pause()
      onSuccess(e, r)
      await wait(2000)
      html5QrCode.current?.resume()
    },
    [onSuccess],
  )

  const getIndexOfBackCamera = useCallback((devices: CameraDevice[]) => {
    if (devices?.length > 1) {
      return devices[1]?.label.includes('Back') ? 1 : 0
    }
  }, [])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (shouldShowSuccess) {
      timer = setTimeout(() => {
        setShouldShowSuccess(false)
      }, 1000)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [shouldShowSuccess])

  const handleRestart = useCallback(async () => {
    if (devicesList?.length >= 1) {
      html5QrCode?.current?.stop()

      const selectedDevice = devicesList?.find((d) => d === selectedCamera)

      if (!selectedDevice) {
        logger().error('No camera device found to restart scanning')
        return
      }

      await Html5Qrcode.getCameras().then(() => {
        html5QrCode?.current?.start(
          selectedDevice.id,
          {
            fps: 10, // Optional, frame per seconds for qr code scanning
            qrbox: { width: 300, height: 300 },
            aspectRatio: 290 / 290,
          },
          handleSuccess,
          onError,
        )
      })
    }
  }, [devicesList, handleSuccess, onError, selectedCamera])

  const handleCameraSwitch = useCallback(async () => {
    if (devicesList?.length > 1) {
      html5QrCode?.current?.stop()

      const selectedIndex = devicesList?.findIndex((d) => d === selectedCamera)
      const selectedDevice = devicesList[selectedIndex === 1 ? 0 : 1]

      if (!selectedDevice) {
        logger().error('No camera device found to switch to', { devicesList })
        return
      }

      await Html5Qrcode.getCameras().then(() => {
        html5QrCode?.current?.start(
          selectedDevice.id,
          {
            fps: 10, // Optional, frame per seconds for qr code scanning
            qrbox: { width: 300, height: 300 },
            aspectRatio: 290 / 290,
          },
          handleSuccess,
          onError,
        )
        setSelectedCamera(selectedDevice)
      })
    }
  }, [devicesList, handleSuccess, onError, selectedCamera])

  const handeInputSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault()
      onSuccess(inputValue)
      setInputValue('')
    },
    [inputValue, onSuccess],
  )

  const handleCameraPermission = useCallback(() => {
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          setDevicesList(devices)
          const backCamera = devices[getIndexOfBackCamera(devices) ?? 0]
          if (backCamera) {
            setSelectedCamera(backCamera)
          }
        }
      })
      .catch((err) => {
        logger().error('An error occurred when attempting to request camera access', err)
      })
  }, [getIndexOfBackCamera])

  useEffect(() => {
    if (devicesList.length >= 1) {
      html5QrCode.current = new Html5Qrcode(/* element id */ 'reader')
      const backCamera = devicesList[getIndexOfBackCamera(devicesList) ?? 0]

      if (!backCamera) {
        logger().error('No back camera found in the devices list', { devicesList })
        return
      }

      html5QrCode.current
        .start(
          backCamera.id,
          {
            fps: 10, // Optional, frame per seconds for qr code scanning
            qrbox: { width: 300, height: 300 },
            aspectRatio: 290 / 290,
          },
          handleSuccess,
          onError,
        )
        .catch((err) => {
          logger().error('An error occurred while attempting to scan a QR code', err)
        })
    }

    return () => {
      if (html5QrCode.current) {
        html5QrCode.current?.stop().then(() => {
          html5QrCode.current?.clear()
        })
      }
    }
  }, [onError, onSuccess, devicesList, handleSuccess, getIndexOfBackCamera])

  return (
    <div>
      <When condition={shouldShowScanner}>
        <div>
          <div className="relative mx-auto flex h-[300px] w-[300px] items-center justify-center">
            <span
              className={classNames(
                'w-16 h-16 absolute -top-1 -left-1',
                shouldShowSuccess ? 'bg-oxide-bright' : 'bg-core-gray-950',
              )}
            />
            <span
              className={classNames(
                'w-16 h-16 absolute -top-1 -right-1',
                shouldShowSuccess ? 'bg-oxide-bright' : 'bg-core-gray-950',
              )}
            />
            <span
              className={classNames(
                'w-16 h-16 absolute -bottom-1 -left-1',
                shouldShowSuccess ? 'bg-oxide-bright' : 'bg-core-gray-950',
              )}
            />
            <span
              className={classNames(
                'w-16 h-16 absolute -bottom-1 -right-1',
                shouldShowSuccess ? 'bg-oxide-bright' : 'bg-core-gray-950',
              )}
            />
            <div className="relative z-10 flex h-[290px] w-[290px] cursor-pointer items-center justify-center border-2 border-white bg-core-gray-200">
              {devicesList.length > 1 && (
                <div
                  onClick={handleCameraSwitch}
                  className="absolute right-2 top-2 z-40 w-fit rounded-full bg-core-gray-400 p-1"
                >
                  <FlipIcon />
                </div>
              )}
              {selectedCamera && <div className="z-20 h-full w-full" id="reader" />}
              {!selectedCamera && (
                <Button onClick={handleCameraPermission} variant="black">
                  Request Camera Access
                </Button>
              )}
            </div>
          </div>
          <Button
            onClick={() => {
              setShouldShowScanner(false)
            }}
            className="mx-auto mt-12 w-full max-w-sm"
            variant="black"
          >
            Enter Code Manually
          </Button>
        </div>
      </When>
      <When condition={!shouldShowScanner}>
        <div className="flex h-[300px] flex-col items-center justify-center">
          <div className="mx-auto mt-12 flex w-full max-w-lg flex-col gap-3 text-left">
            <TitleMD>Angel Code</TitleMD>
            <form onSubmit={handeInputSubmit}>
              <input
                placeholder="12345"
                onChange={(e) => setInputValue(e.target.value)}
                value={inputValue}
                className="w-full rounded-md border p-2"
                type="text"
              />
              <Button className="mt-3 w-full" type="submit" variant="black">
                Enter
              </Button>
            </form>
          </div>
        </div>
        <Button
          onClick={async () => {
            setShouldShowScanner(true)
            setInputValue('')
            await handleRestart()
          }}
          className="mx-auto mt-12 w-full max-w-sm"
          variant="black"
        >
          Switch To Scanner
        </Button>
      </When>
    </div>
  )
}
